if (!customElements.get('dl-catalog-carousel')) {
  customElements.define('dl-catalog-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-dl-track]');
      this.controls = this.querySelector('[data-dl-carousel-controls]');
      this.counter = this.querySelector('[data-dl-position]');
      this.media = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.serial = 0;
      this.onMediaChange = () => this.refresh();
      this.media.addEventListener('change', this.onMediaChange);
      this.querySelectorAll('[data-dl-direction]').forEach(button => {
        button.addEventListener('click', () => this.move(Number(button.dataset.dlDirection)), { signal });
      });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.onScroll();
        });
      }, { passive: true, signal });
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.measure());
      });
      this.resizeObserver.observe(this.track);
      this.refresh();
    }

    disconnectedCallback() {
      this.connected = false;
      this.controller?.abort();
      this.media?.removeEventListener('change', this.onMediaChange);
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.measureFrame);
    }

    copyCard(card, side) {
      const clone = card.cloneNode(true);
      clone.dataset.dlClone = side;
      clone.removeAttribute('data-dl-topic');
      clone.removeAttribute('data-dl-title');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('dialog').forEach(dialog => dialog.remove());
      const suffix = '-loop-' + side + '-' + (++this.serial);
      const ids = new Map();
      [clone, ...clone.querySelectorAll('[id]')].forEach(element => {
        if (!element.id) return;
        ids.set(element.id, element.id + suffix);
        element.id += suffix;
      });
      [clone, ...clone.querySelectorAll('*')].forEach(element => {
        [...element.attributes].forEach(attribute => {
          if (attribute.name.startsWith('data-shopify-editor')) element.removeAttribute(attribute.name);
        });
        ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attribute => {
          const value = element.getAttribute(attribute);
          if (value) element.setAttribute(attribute, value.split(' ').map(id => ids.get(id) || id).join(' '));
        });
      });
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(element => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    refresh() {
      if (!this.track) return;
      this.track.querySelectorAll('[data-dl-clone]').forEach(clone => clone.remove());
      this.cards = [...this.track.querySelectorAll(':scope > .dl-card[data-dl-topic]')].filter(card => !card.hidden);
      this.index = 0;
      this.looping = this.media.matches && this.cards.length > 1;
      this.controls.hidden = !this.looping;
      this.track.setAttribute('tabindex', this.looping ? '0' : '-1');
      this.track.dataset.loop = String(this.looping);
      if (this.looping) {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.cards.forEach(card => {
          before.append(this.copyCard(card, 'before'));
          after.append(this.copyCard(card, 'after'));
        });
        this.track.prepend(before);
        this.track.append(after);
      }
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => this.measure());
      this.updateCounter();
    }

    position(card) {
      return card.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    }

    measure() {
      if (!this.looping || !this.cards.length) {
        this.track.scrollLeft = 0;
        return;
      }
      const first = this.cards[0];
      const next = this.cards[1];
      const after = this.track.querySelector('[data-dl-clone="after"]');
      this.start = this.position(first);
      this.step = this.position(next) - this.start;
      this.period = this.position(after) - this.start;
      this.jump(this.start + this.index * this.step);
    }

    jump(left) {
      this.track.classList.add('dl-recentering');
      this.track.scrollTo({ left, behavior: 'instant' });
      requestAnimationFrame(() => this.track.classList.remove('dl-recentering'));
    }

    onScroll() {
      if (!this.looping || !this.period || !this.step) return;
      let left = this.track.scrollLeft;
      if (left < this.start - this.step / 2) {
        left += this.period;
        this.jump(left);
      } else if (left >= this.start + this.period - this.step / 2) {
        left -= this.period;
        this.jump(left);
      }
      const count = this.cards.length;
      this.index = ((Math.round((left - this.start) / this.step) % count) + count) % count;
      this.updateCounter();
    }

    move(direction) {
      if (!this.looping || !this.step) return;
      const closest = Math.round((this.track.scrollLeft - this.start) / this.step);
      this.track.scrollTo({
        left: this.start + (closest + direction) * this.step,
        behavior: this.reducedMotion.matches ? 'instant' : 'smooth'
      });
    }

    updateCounter() {
      if (this.counter) this.counter.textContent = (this.cards.length ? this.index + 1 : 0) + ' / ' + this.cards.length;
    }
  });
}

if (!customElements.get('dl-library-home')) {
  customElements.define('dl-library-home', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const carousel = this.querySelector('dl-catalog-carousel');
      const search = this.querySelector('[data-dl-search]');
      const filters = [...this.querySelectorAll('[data-dl-filter]')];
      const cards = [...this.querySelectorAll('.dl-card[data-dl-topic]')];
      let active = 'all';
      const apply = () => {
        const term = search.value.toLowerCase().trim();
        let visible = 0;
        cards.forEach(card => {
          const show = (active === 'all' || card.dataset.dlTopic === active) && card.dataset.dlTitle.toLowerCase().includes(term);
          card.hidden = !show;
          if (show) visible++;
        });
        this.querySelector('[data-dl-empty]').hidden = visible > 0;
        this.querySelector('[data-dl-count]').textContent = visible + ' / ' + cards.length;
        filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.dlFilter === active)));
        carousel.refresh();
      };
      filters.forEach(button => button.addEventListener('click', () => {
        active = button.dataset.dlFilter;
        apply();
      }, { signal }));
      search.addEventListener('input', apply, { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-dl-open]');
        if (!button) return;
        const dialog = this.querySelector('#' + CSS.escape(button.dataset.dlOpen));
        if (dialog && !dialog.open) dialog.showModal();
      }, { signal });
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.querySelector('[data-dl-close]')?.addEventListener('click', () => dialog.close(), { signal });
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, { signal });
      });
      apply();
    }

    disconnectedCallback() {
      this.connected = false;
      this.controller?.abort();
    }
  });
}
